import React from 'react';
import { Clock, Loader2, Send, Sparkles } from 'lucide-react';
import { Button } from '../../../components/ui';
import { cn } from '../../../lib/utils';
import type { ThemeColors } from '../../../contexts/ThemeContext';
import EmailPresenceIndicator from './EmailPresenceIndicator';
import type { EmailPresenceUser } from '../hooks/useEmailPresence';
import { formatDraftAutosaveCountdown, type ComposeDraftAutosaveStatus } from '../hooks/useComposeDraftAutosave';
import ComposeSendProgressBanner, {
  composeSendButtonLabel,
  isActiveSendPhase,
  type ComposeSendProgress,
} from './ComposeSendProgressBanner';

export type { ComposeSendPhase, ComposeSendProgress } from './ComposeSendProgressBanner';
export { composeSendButtonLabel } from './ComposeSendProgressBanner';

const ICON = { size: 16, strokeWidth: 1.75 } as const;

export interface ComposeHeaderProps {
  colors: ThemeColors;
  backButtonRef: React.Ref<HTMLButtonElement>;
  onBack: () => void;
  presenceUsers: EmailPresenceUser[];
  showDraftStatus: boolean;
  draftAutosaveStatus: ComposeDraftAutosaveStatus;
  secondsUntilSave: number;
  canScheduleSend: boolean;
  isSending: boolean;
  sendProgress?: ComposeSendProgress | null;
  /** True when the current send includes attachments (shows Prepare step). */
  sendHasAttachments?: boolean;
  /** Cancel in-flight send (prepare / upload / deliver). */
  onCancelSend?: () => void;
  attachmentsOverLimit: boolean;
  onSchedule: () => void;
  onAiMouseDown: () => void;
  onAi: () => void;
  aiPanelOpen?: boolean;
  /** When false, AI button is visible but disabled (setting off). */
  aiEmailsEnabled?: boolean;
}

const ComposeHeader: React.FC<ComposeHeaderProps> = ({
  colors,
  backButtonRef,
  onBack,
  presenceUsers,
  showDraftStatus,
  draftAutosaveStatus,
  secondsUntilSave,
  canScheduleSend,
  isSending,
  sendProgress,
  sendHasAttachments = false,
  onCancelSend,
  attachmentsOverLimit,
  onSchedule,
  onAiMouseDown,
  onAi,
  aiPanelOpen = false,
  aiEmailsEnabled = true,
}) => {
  const sendLabel = isSending ? composeSendButtonLabel(sendProgress) : 'Send';
  const showProgress = isSending && isActiveSendPhase(sendProgress);

  return (
    <header className={cn('flex-shrink-0 border-b', colors.surface, colors.border)}>
      <div className="flex min-h-[3rem] min-w-0 items-center gap-3 px-4 py-2 sm:px-5">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            ref={backButtonRef}
            variant="ghost"
            onClick={onBack}
            className="flex-shrink-0 rounded-lg p-2"
            title="Back to emails"
            disabled={isSending}
          >
            <svg
              className={cn('h-5 w-5', colors.textSecondary)}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </Button>
          <EmailPresenceIndicator users={presenceUsers} className="flex-shrink-0" />
        </div>

        {showDraftStatus && !isSending && (
          <div
            className={cn(
              'hidden max-w-[14rem] flex-shrink-0 items-center rounded-full border px-2.5 py-1 text-xs sm:flex',
              draftAutosaveStatus === 'error'
                ? 'border-red-300/60 bg-red-500/10 text-red-700 dark:text-red-300'
                : cn(colors.border, colors.surfaceHover, colors.textMuted),
            )}
            aria-live="polite"
          >
            {draftAutosaveStatus === 'saving'
              ? 'Saving draft…'
              : draftAutosaveStatus === 'error'
                ? 'Draft save failed'
                : `Draft saves in ${formatDraftAutosaveCountdown(secondsUntilSave)}`}
          </div>
        )}

        <div className="min-w-2 flex-1" aria-hidden />

        <Button
          type="button"
          variant="ghost"
          onMouseDown={aiEmailsEnabled && !isSending ? onAiMouseDown : undefined}
          onClick={aiEmailsEnabled && !isSending ? onAi : undefined}
          disabled={!aiEmailsEnabled || isSending}
          className={cn(
            'flex-shrink-0 rounded-lg p-2',
            aiEmailsEnabled
              ? 'text-violet-600 dark:text-violet-400'
              : cn(colors.textMuted, 'cursor-not-allowed opacity-50'),
            aiPanelOpen && aiEmailsEnabled && 'bg-violet-500/15',
            !aiPanelOpen && aiEmailsEnabled && !isSending && colors.interactiveHover,
          )}
          title={
            aiEmailsEnabled
              ? 'AI Assistant (rewrite, proofread, reply, etc.)'
              : 'AI email assistance is disabled in application settings'
          }
          aria-label="AI Assistant"
          aria-pressed={aiPanelOpen}
        >
          <Sparkles className="h-5 w-5" {...ICON} aria-hidden />
        </Button>

        <div
          className={cn(
            'flex flex-shrink-0 items-center gap-2 rounded-lg border-l px-1 py-0.5 pl-2 sm:px-2 sm:pl-3',
            colors.border,
            'bg-primary/5 dark:bg-primary/10',
          )}
        >
          {canScheduleSend && (
            <Button
              type="button"
              variant="outline"
              disabled={isSending || attachmentsOverLimit}
              onClick={onSchedule}
              className={cn(
                'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
                colors.border,
                colors.text,
                colors.surface,
              )}
              title="Schedule send"
            >
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" {...ICON} aria-hidden />
              Schedule
            </Button>
          )}
          <Button
            type="submit"
            form="compose-email-form"
            variant="default"
            disabled={isSending || attachmentsOverLimit}
            className={cn(
              'flex min-w-[7.5rem] items-center justify-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-medium shadow-sm disabled:cursor-not-allowed disabled:opacity-50',
              colors.primary,
              colors.primaryText,
              colors.primaryHover,
            )}
          >
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" {...ICON} aria-hidden />
                {sendLabel}
              </>
            ) : (
              <>
                <Send className="h-4 w-4" {...ICON} aria-hidden />
                Send
              </>
            )}
          </Button>
        </div>
      </div>

      {showProgress && (
        <ComposeSendProgressBanner
          colors={colors}
          progress={sendProgress}
          hasAttachments={sendHasAttachments}
          onCancel={onCancelSend}
        />
      )}
    </header>
  );
};

export default ComposeHeader;
